import type { EJobStatus } from '@quesmed/types/models';
import { type Key, useMemo } from 'react';
import { BiDotsHorizontal, BiDuplicate } from 'react-icons/bi';
import { BsCopy } from 'react-icons/bs';
import { FaEdit } from 'react-icons/fa';
import { FaEye, FaTrash } from 'react-icons/fa6';
import { EFormMode } from '@/config/globals';
import { iconClasses } from '@/config/job';
import { ActionDropdown, cn, toaster } from '@/core/ui';
// import { useAuth } from '@/store/auth';
import { useJob } from '@/store/job';

const JobActions = {
  COPY: {
    key: 'copy',
    className: 'text-xs py-1',
    startContent: <BsCopy className={cn(iconClasses, '')} />,
    title: 'Copy',
    color: 'default',
  },
  VIEW: {
    key: 'view',
    className: 'text-xs py-1',
    startContent: <FaEye className={cn(iconClasses)} />,
    title: 'View',
  },
  EDIT: {
    key: 'edit',
    className: 'text-xs py-1',
    startContent: <FaEdit className={cn(iconClasses)} />,
    title: 'Edit',
  },
  DUPLICATE: {
    key: 'duplicate',
    className: 'text-xs py-1',
    startContent: <BiDuplicate className={cn(iconClasses)} />,
    title: 'Duplicate',
  },
  DELETE: {
    key: 'delete',
    className: 'text-xs py-1',
    startContent: <FaTrash className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Delete',
    color: 'danger',
  },
};

export const JobItemActions = ({
  jobId,
  name,
  // status,
  // createdBy,
  trial,
}: {
  jobId: string;
  name: string;
  status: EJobStatus;
  createdBy: number;
  trial?: boolean;
}) => {
  // const isWriter = useAuth.use.isWriter();
  // const { id: currentUserId } = useAuth.use.tokenDecoded() || {};
  const setCreateModal = useJob.use.setCreateModal();
  const setJobId = useJob.use.setJobId();
  const setConfirmModal = useJob.use.setConfirmModal();

  // const isOwner = currentUserId === createdBy;

  const handleAction = (key: Key) => {
    switch (key) {
      case 'view': {
        setJobId(jobId);
        setCreateModal(true, EFormMode.VIEW);
        break;
      }
      case 'edit': {
        setJobId(jobId);
        setCreateModal(true, EFormMode.EDIT);
        break;
      }
      case 'duplicate': {
        setConfirmModal(
          true,
          'Duplicate Job',
          <div className='text-sm flex flex-col gap-2'>
            <span>
              Are you sure want to duplicate this job with name <b>{name}</b>?
            </span>
            <span className='text-default-500'>
              This will also duplicate all records belonging to this job. The duplicated job will have status{' '}
              <b>Ongoing</b> and all duplicated records will have status <b>Draft</b>.<br />
            </span>
            <span className='text-default-500'>
              The job members will remain the same, and the authors of the records will also remain the same.
            </span>
          </div>,
          'duplicate',
          jobId,
        );
        break;
      }
      case 'delete': {
        setConfirmModal(
          true,
          'Delete Job',
          <>
            Are you sure want to delete this job with name <b>{name}</b>?
          </>,
          'delete',
          jobId,
        );
        break;
      }
      case 'copy': {
        navigator.clipboard.writeText(jobId);
        toaster.success('Job ID copied to clipboard');
        break;
      }
      default:
        break;
    }
  };

  const actionItems = useMemo(() => {
    if (trial) {
      return [JobActions.COPY, JobActions.VIEW];
    }

    return [JobActions.COPY, JobActions.VIEW, JobActions.EDIT, JobActions.DUPLICATE, JobActions.DELETE];

    // if (status >= EJobStatus.TESTING) {
    //   return ['edit'];
    // }

    // if (isWriter && !isOwner) {
    //   return ['edit', 'duplicate', 'delete'];
    // }
  }, [trial]);

  return (
    <ActionDropdown
      offset={0}
      classNames={{
        content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
      }}
      onAction={handleAction}
      // disabledKeys={disabledKeys}
      items={actionItems}
    >
      <div className='hover:bg-content2 flex justify-center items-center rounded-md px-1 py-0.5 cursor-pointer text-default-400'>
        <BiDotsHorizontal className='size-5 cursor-pointer text-default-400' />
      </div>
    </ActionDropdown>
  );
};
